import * as R from 'ramda';
import { fetchWithCometFallback, MetricsLambdaFetch } from '../../utils/fetch-utils';
import { MODEL_BUCKET } from './geo-mmm-constants';
import {
  CsvRow,
  loadRows,
  parseNum,
  parseNumOrNull
} from '../MMM/DtcCommerceMMM/utils/dtc-commerce-mmm-data-loader';

// Meridian Geo MMM runs ship as per-file `*_meridian.csv.gz` outputs instead of the old 10-sheet
// Excel workbook. This loader reads those files and reshapes them into the same sheet-shaped rows
// the workbook produced, so the Results components serve both run formats unchanged. Mapping per
// the econ technical reference attached to DEV-25207.

export type GeoMmmSheets = Record<string, any[]>;

export interface GeoMmmMeridianFiles {
  modelOverview: CsvRow[];
  featureMetadata: CsvRow[];
  mapeByGeo: CsvRow[];
  actualVsPredicted: CsvRow[];
  spendEffectOverTime: CsvRow[];
  geoSpendEffectOverTime: CsvRow[];
  marginalDecision: CsvRow[];
  responseCurves: CsvRow[];
  adstockDecayCurves: CsvRow[];
}

// geo_mmm_info.source for a Meridian run points at the run folder's model_overview file;
// workbook runs point at the .xlsx.
export const isMeridianSource = (source?: string): boolean =>
  !!source && !source.toLowerCase().endsWith('.xlsx');

// `[tinuiti-mmm-results/]mmm/{branch}/{company}/{kpi}/{date}/<file>`. The company segment is the
// S3-normalized slug, so callers pass the page's company instead.
export const parseMeridianSource = (
  source: string
): { branch: string; kpi: string; date: string } => {
  const prefix = `${MODEL_BUCKET}/`;
  const parts = (source.startsWith(prefix) ? source.slice(prefix.length) : source).split('/');
  const mmmIndex = parts.indexOf('mmm');
  if (mmmIndex === -1 || parts.length < mmmIndex + 5) {
    throw new Error(`Unrecognized Geo MMM source path: ${source}`);
  }
  const [branch, , kpi, date] = parts.slice(mmmIndex + 1);
  return { branch, kpi, date };
};

const isRaw = (row: CsvRow): boolean => row.attribution_type === 'raw';

// Spreads a long-over-estimate_type group (ci_lo/mean/ci_hi rows) into one value per estimate.
// A blank value stays null, e.g. an unbounded ci_hi on spend_needed_for_one_more_kpi.
const byEstimate = (rows: CsvRow[], column: string): Record<string, number | null> =>
  Object.fromEntries(rows.map(row => [row.estimate_type, parseNumOrNull(row[column])]));

// Sum(contribution) / sum(spend) over posterior-mean, direct (raw) paid media. Covariates carry
// the whole baseline with zero spend, so leaving them in inflates the ratio several-fold.
export const computeTotalRoi = (spendEffectOverTime: CsvRow[]): number => {
  const rows = spendEffectOverTime.filter(
    row => row.estimate_type === 'mean' && isRaw(row) && row.media_type !== 'covariates'
  );
  const spend = R.sum(rows.map(row => parseNum(row.spend)));
  return spend ? R.sum(rows.map(row => parseNum(row.contribution))) / spend : 0;
};

const buildSummaryMetrics = (modelOverview: CsvRow[], spendEffectOverTime: CsvRow[]) => {
  const param = (name: string): string =>
    modelOverview.find(row => row.parameter === name)?.value ?? '';
  return [
    { metric: 'Start Date', value: param('train_start_date') },
    { metric: 'End Date', value: param('test_end_date') },
    { metric: 'Outcome', value: param('kpi_type') === 'revenue' ? 'Revenue' : 'Volume' },
    { metric: 'Average Weekly Spend', value: parseNum(param('mean_spend')) },
    // test_size is the held-out fraction; the page expects a percentage.
    { metric: 'Holdout Percentage', value: parseNum(param('test_size')) * 100 },
    // Already a percent (5.92 = 5.92%).
    { metric: 'wMAPE', value: parseNum(param('test_wmape')) },
    { metric: 'Total ROI', value: computeTotalRoi(spendEffectOverTime) },
    // n_weeks, not n_observations (geo-weeks for a geo model).
    { metric: 'Length of Time Series', value: parseNum(param('n_weeks')) }
  ];
};

const buildRoiTable = (marginalDecision: CsvRow[]) =>
  Object.values(R.groupBy(row => row.name, marginalDecision.filter(isRaw))).map(group => {
    const rows = group ?? [];
    const allTimeRoas = byEstimate(rows, 'all_time_roas');
    const previousQtrRoas = byEstimate(rows, 'previous_qtr_roas');
    const nextDollar = byEstimate(rows, 'next_dollar_effectiveness');
    const response = byEstimate(rows, 'response_to_previous_qtr_average_spend');
    // Read directly rather than inverting next_dollar_effectiveness — the bounds are mirrored.
    const marginalCpa = byEstimate(rows, 'spend_needed_for_one_more_kpi');
    return {
      channel: rows[0].name,
      Mean: allTimeRoas.mean,
      'Lower Bound': allTimeRoas.ci_lo,
      'Upper Bound': allTimeRoas.ci_hi,
      'Mean (LQ)': previousQtrRoas.mean,
      'Lower Bound (LQ)': previousQtrRoas.ci_lo,
      'Upper Bound (LQ)': previousQtrRoas.ci_hi,
      'Next Best Dollar Effectiveness': nextDollar.mean,
      'Lower Bound (Marg)': nextDollar.ci_lo,
      'Upper Bound (Marg)': nextDollar.ci_hi,
      pred_median: response.mean,
      pred_median_low: response.ci_lo,
      pred_median_high: response.ci_hi,
      median_spend_lq: parseNumOrNull(rows[0].previous_qtr_average_spend),
      'Next Dollar Marginal CPA': marginalCpa.mean,
      'Lower Bound (Marg CPA)': marginalCpa.ci_lo,
      'Upper Bound (Marg CPA)': marginalCpa.ci_hi
    };
  });

const buildResponseCurves = (responseCurves: CsvRow[]) =>
  Object.values(R.groupBy(row => `${row.name}|${row.spend}`, responseCurves.filter(isRaw))).map(
    group => {
      const rows = group ?? [];
      const response = byEstimate(rows, 'response');
      return {
        channel: rows[0].name,
        // Already weekly, unlike the workbook's whole-window totals.
        spend: parseNum(rows[0].spend),
        // The current-spend marker the chart looks for (spend_multiplier === 1 in the workbook).
        spend_multiplier: parseNum(rows[0].previous_qtr_average_value) === 1 ? 1 : null,
        ci_lo: response.ci_lo,
        mean: response.mean,
        ci_hi: response.ci_hi
      };
    }
  );

const buildAdstock = (adstockDecayCurves: CsvRow[]) =>
  Object.values(R.groupBy(row => `${row.name}|${row.period}`, adstockDecayCurves)).map(group => {
    const rows = group ?? [];
    const carryover = byEstimate(rows, 'spend_carryover');
    return {
      channel: rows[0].name,
      time_units: parseNum(rows[0].period),
      ci_lo: carryover.ci_lo,
      mean: carryover.mean,
      ci_hi: carryover.ci_hi
    };
  });

const buildAttributionOverTime = (spendEffectOverTime: CsvRow[]) =>
  spendEffectOverTime
    .filter(isRaw)
    // Covariates is the baseline; the chart reads a single mean Baseline row per date.
    .filter(row => row.media_type !== 'covariates' || row.estimate_type === 'mean')
    .map(row => ({
      channel: row.media_type === 'covariates' ? 'Baseline' : row.name,
      time: row.date,
      metric: row.estimate_type,
      incremental_outcome: parseNum(row.contribution),
      spend: parseNum(row.spend)
    }));

// geo_spend_effect_over_time is long over attribution_type and estimate_type; only raw/mean is used.
// `name` is already the channel key. Each DMA's Covariates row is its baseline (zero spend), so it
// is dropped from Media Spend and relabelled Baseline in Attribution by Geo. The `other` geo
// stays; consumers drop it from the map themselves.
const COVARIATES = 'Covariates';

export const buildGeoSheets = (
  geoSpendEffectOverTime: CsvRow[],
  featureMetadata: CsvRow[] = []
) => {
  // The lambda normalizes v1 files to this shape but their `name` is still a feature id
  // (featid1007); cleaned_name_lookup maps it to the composite name. v2 names pass through.
  const nameByFeatureId: Record<string, string> = Object.fromEntries(
    featureMetadata.map(row => [row.meridian_channel, row.name])
  );
  const channelName = (row: CsvRow): string => nameByFeatureId[row.name] ?? row.name;
  const rawMean = geoSpendEffectOverTime.filter(row => isRaw(row) && row.estimate_type === 'mean');
  const mediaSpendByGeoWeek: Record<string, Record<string, string | number>> = {};
  rawMean
    .filter(row => row.name !== COVARIATES)
    .forEach(row => {
      const name = channelName(row);
      const key = `${row.geo}|${row.date}`;
      mediaSpendByGeoWeek[key] ??= { geo: row.geo, time: row.date };
      mediaSpendByGeoWeek[key][name] = parseNum(row.spend);
    });
  const attributionByGeo = rawMean.map(row => ({
    geo: row.geo,
    channel: row.name === COVARIATES ? 'Baseline' : channelName(row),
    metric: row.estimate_type,
    incremental_outcome: parseNum(row.contribution),
    spend: parseNum(row.spend)
  }));
  return { mediaSpend: Object.values(mediaSpendByGeoWeek), attributionByGeo };
};

export const buildGeoMmmSheets = (files: GeoMmmMeridianFiles): GeoMmmSheets => {
  const { mediaSpend, attributionByGeo } = buildGeoSheets(
    files.geoSpendEffectOverTime,
    files.featureMetadata
  );
  return {
    'Summary Metrics': buildSummaryMetrics(files.modelOverview, files.spendEffectOverTime),
    'MAPE by Geo': files.mapeByGeo.map(row => ({ geo: row.geo, mape: parseNum(row.mape) })),
    Fit: files.actualVsPredicted.map(row => ({
      geo: row.geo,
      time: row.date,
      metric: row.estimate_type,
      expected: parseNum(row.expected_kpi),
      actual: parseNum(row.actual_kpi),
      baseline: parseNum(row.baseline_prediction)
    })),
    'Media Spend': mediaSpend,
    'Attribution Over Time': buildAttributionOverTime(files.spendEffectOverTime),
    'ROI Table': buildRoiTable(files.marginalDecision),
    'Response Curves': buildResponseCurves(files.responseCurves),
    Adstock: buildAdstock(files.adstockDecayCurves),
    'Attribution by Geo': attributionByGeo,
    Grouping: files.featureMetadata.map(row => ({
      channel: row.channel,
      platform: row.platform,
      tactic: row.tactic,
      brand_nonbrand: row.brand_nonbrand
    }))
  };
};

export const loadGeoMmmMeridianSheets = async (
  company: string,
  source: string
): Promise<GeoMmmSheets> => {
  const { branch, kpi, date } = parseMeridianSource(source);
  const ctx = { client: company, branch, kpi, timestamp: date, outputFileVersion: '2' };
  const [
    modelOverview,
    featureMetadata,
    mapeByGeo,
    actualVsPredicted,
    spendEffectOverTime,
    geoSpendEffectOverTime,
    marginalDecision,
    responseCurves,
    adstockDecayCurves
  ] = await Promise.all([
    loadRows(ctx, '/getModelOverviewData'),
    loadRows(ctx, '/getFeatureMetadata'),
    loadRows(ctx, '/getMapeByGeoData'),
    loadRows(ctx, '/getActualVsPredictedData'),
    loadRows(ctx, '/getSpendEffectOverTimeData'),
    // Every DMA x week x channel — big enough to need the Comet fallback.
    fetchWithCometFallback<Record<string, string>, CsvRow[]>({
      service: MetricsLambdaFetch,
      standardEndpoint: '/getGeoSpendEffectOverTimeData',
      input: { company, branch, kpi, date },
      method: 'get'
    }),
    loadRows(ctx, '/getMarginalDecisionData'),
    loadRows(ctx, '/getResponseCurvesData'),
    loadRows(ctx, '/getDecayCurvesData')
  ]);
  return buildGeoMmmSheets({
    modelOverview,
    featureMetadata,
    mapeByGeo,
    actualVsPredicted,
    spendEffectOverTime,
    geoSpendEffectOverTime,
    marginalDecision,
    responseCurves,
    adstockDecayCurves
  });
};
